import React, {
  Suspense, lazy, useEffect,
} from 'react';
import { Spinner } from '@printdeal/ui-library';
import {
  getPricePivot, getAttributes, ProductContent, SelectVatMode, ProductDiscount,
  getSelectedPriceAttributes, useSelectorMachineContext, pricePivotAttributes, useMatchesState,
} from '@printdeal/product-selector';
import { isCustomerProgramReseller, useSegmentation } from '@printdeal/segmentation/react';
import { useSelector } from 'react-redux';
import { dispatchDataLayerEntry } from '../../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { getPriceGroups } from '../../../../../helpers/PricesHelper';
import ProductUnavailableAlert from '../ProductUnavailableAlert';
import { PriceCheckAlert } from '../PriceCheckAlert';
import { RootState } from '../../../../../store';
import { selectors as authSelectors } from '../../../../../store/slices/authSlice';

const Prices = lazy(() =>
  import(/* webpackChunkName: "prices" */ '@printdeal/product-selector').then((module) => ({ default: module.PricesComponent })));

interface Props {
  product: ProductContent;
  shippingAddress: {
    CountryCode: string;
    PostalCode: string;
  }
  customerGroupId?: string;
  contentfulPageId: string;
  productDiscounts: ProductDiscount[]
}

export const Pricing = ({
  product, shippingAddress, customerGroupId, contentfulPageId, productDiscounts,
}: Props) => {
  const configurationAttributes = useSelectorMachineContext((state) => state.context.attributes);
  const selection = useSelectorMachineContext((state) => state.context.selection);
  const price = useSelectorMachineContext((state) => state.context.price);
  const groupSelection = useSelectorMachineContext((state) => state.context.group);
  const productAttributes = useSelectorMachineContext((state) => state.context.productAttributes);
  const isSelectionComplete = useSelectorMachineContext((state) => state.matches({ STARTED: 'SELECTION COMPLETE' }));
  const isFetchingServicePrices = useMatchesState({ STARTED: 'FETCHING SERVICE PRICES' });
  const isFetchingMerchantRules = useMatchesState({ STARTED: 'FETCHING MERCHANT RULES' });
  const selectorSelection = useSelectorMachineContext((state) => state.context.selection);
  const totalQuantity = useSelectorMachineContext((state) => state.context.totalQuantity);
  const isPimProduct = useSelectorMachineContext((state) => state.context.product?.isPimProduct);
  const hasInsufficientInventory = useMatchesState({ STARTED: { 'FETCHING DATA': { 'ARRIVAL DATES': 'INSUFFICIENT INVENTORY' } } });
  const { data: segmentationData } = useSegmentation();
  const isResellerPartOfCustomerProgram = isCustomerProgramReseller(
    segmentationData?.isReseller, segmentationData?.isPartOfCustomerProgram,
  );
  const isAuthLoading = useSelector((state: RootState) => authSelectors.isAuthLoading(state));
  const shouldRenderPriceTable = isSelectionComplete || isFetchingServicePrices || isFetchingMerchantRules || price;

  useEffect(() => {
    if (hasInsufficientInventory) {
      dispatchDataLayerEntry('selector', 'warning', { msg: 'Insufficient_inventory' });
    }
  }, [hasInsufficientInventory]);

  const { sort } = getPricePivot(configurationAttributes);
  // check auth for the labels in the price table
  if (shouldRenderPriceTable && !isAuthLoading) {
    const preselectedPriceAttributes = selection.Quantity && {
      ...getSelectedPriceAttributes(configurationAttributes, selection),
      Quantity: parseInt(selection.Quantity, 10),
    };

    const inventoryCheckEnabled = productAttributes?.find(({ key }) => key === 'InventoryCheck')?.resolvedValue === 'Yes';

    return (
      <div className="pd-mb-6 pd-flex pd-flex-col pd-gap-4">
        <SelectVatMode className="pd-self-end" />
        {customerGroupId && isResellerPartOfCustomerProgram && (
          <PriceCheckAlert />
        )}
        <Suspense fallback={<Spinner className="pd-my-2" />}>
          <Prices
            shippingAddress={shippingAddress}
            product={product}
            selectionAttributes={getAttributes(selection, [...pricePivotAttributes, 'Quantity'])}
            group={getPriceGroups(groupSelection)}
            sort={sort}
            preselectedPriceAttributes={preselectedPriceAttributes || undefined}
            inventoryCheckEnabled={inventoryCheckEnabled}
            customerGroupId={customerGroupId}
            contentfulId={contentfulPageId}
            isReseller={isResellerPartOfCustomerProgram}
            productDiscounts={productDiscounts}
            merchandisedQuantities={isPimProduct ? product.customProductQuantities?.map(Number) : undefined}
          />
          <ProductUnavailableAlert
            contentfulId={contentfulPageId}
            product={product}
            currentSelection={selectorSelection}
            totalQuantity={totalQuantity || 0}
            groupSelection={groupSelection}
          />
        </Suspense>
      </div>
    );
  }

  return null;
};
